<template>
  <div class="life-story">
    <block-hero
      :title="page.title"
      :description="page.abstract">
      <!-- <template #content>
        <cta
          text="inviaci il tuo racconto"
          variant="solid"
          color="red"
          :show-icon="true"
          :animated-icon="true"
          aria-label="inviaci il tuo racconto"
          @click="handleModal" />
      </template> -->
    </block-hero>

    <div class="life-story__new-story">
      <div class="l-wrapper">
        <cta
          :text="$t('forms.lifeStories.send_story')"
          variant="solid"
          color="red"
          :show-icon="true"
          :animated-icon="true"
          :aria-label="$t('forms.lifeStories.send_story')"
          @click="handleModal" />
      </div>
    </div>

    <block-image
      v-if="page.image"
      priority
      :image="page.image" />

    <block-image-text
      v-if="page.editorialText"
      image-side="left"
      :image-desktop="page.editorialImage"
      :description="page.editorialText"
      :title-image="[{
        src: '/images/pngs/logo-venetacucine-racconti-vita.png',
      }]" />

    <life-story-listing
      v-if="exists(allstories)"
      :stories="allstories" />

    <teleport to="body">
      <base-modal
        v-if="modal"
        class="life-story__modal"
        fullscreen
        close-inside
        :show="setupModal"
        @close="setupModal = false"
        @modal:mounted="onModalMount"
        @modal:after-leave="handleCloseModal">
        <template #default>
          <loading-indicator
            v-if="loading"
            infinite />
          <form-life-story
            v-if="!loading"
            :kitchens="kitchens"
            :stores="stores"
            @submit:successful="onSuccessfulSubmit" />
        </template>
      </base-modal>
    </teleport>
  </div>
</template>

<script setup>
  import transitions from '~/animations/pages'
  import { ALL_KITCHENS } from '~/graphql/pages/kitchen'
  import { LIFE_STORY_INDEX } from '~/graphql/pages/life-story'
  import { ALL_STORES } from '~/graphql/stores/index'
  import { useLifeStoriesStore } from '~/store/life-stories'
  import { pushDataLayer } from '~/utils/tracking'

  const { locale } = useI18n()
  const lifestories = useLifeStoriesStore()

  const {
    previewToken, previewParam, previewTimestamp
  } = usePreview()

  const { data: page, error } = await useAsyncData(
    `lifestories-index-${locale.value}`,
    () =>
      useApi(
        LIFE_STORY_INDEX,
        {},
        {
          previewToken: previewToken.value,
          previewParam: previewParam.value
        }
      ),
    {
      transform: r => r.page,
      watch: [ previewTimestamp ]
    }
  )

  // Handle fetch errors
  if (error.value || !page.value) {
    console.error('Error fetching lifestories data:', error.value)

    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found'
    })
  }

  if (!exists(lifestories.stories)) {
    await lifestories.getStories()
  }

  const setupModal = ref(false)
  const modal = ref(false)
  const kitchens = ref([])
  const stores = ref([])
  const loading = ref(false)
  const { bus } = useNuxtApp()
  const route = useRoute()

  const allstories = computed(() => lifestories.stories)

  const handleModal = () => {
    modal.value = true
    setupModal.value = true

    pushDataLayer({
      event: 'openForm-racconti-vita'
    })
  }

  const handleCloseModal = () => {
    modal.value = false
  }

  const onModalMount = async () => {
    if (exists(kitchens.value)) { return }

    loading.value = true

    const [ { kitchens: allkitchens }, { stores: allstores } ] = await Promise.all([ await useApi(ALL_KITCHENS), await useApi(ALL_STORES) ])

    kitchens.value = allkitchens?.map(v => ({
      ...v, label: v.title, value: v.id
    }))

    stores.value = allstores?.map(v => ({
      ...v, label: v.title, value: v.id
    }))

    loading.value = false
  }

  const onSuccessfulSubmit = async () => {
    await lifestories.getStories()
    setupModal.value = false
  }

  if (route.query?.form == 1) {
    bus.on('page:after-enter', () => {
      handleModal()
    })
  }

  // Scroll back to the previously visited story card after returning from detail
  const scrollToLastVisited = async () => {
    const slug = lifestories.lastVisitedSlug
    if (!slug) return

    await nextTick()
    const el = document.querySelector(`[data-slug="${slug}"]`)
    if (!el) {
      lifestories.lastVisitedSlug = null
      return
    }

    bus.emit('scroll:scroll-to', {
      target: el,
      options: { offset: -150, lock: false }
    })

    lifestories.lastVisitedSlug = null
  }

  bus.on('page:after-enter', scrollToLastVisited)
  onBeforeUnmount(() => bus.off('page:after-enter', scrollToLastVisited))

  useSeoData(page)

  definePageMeta({
    pageTransition: {
      name: 'page-transition',
      mode: 'out-in',
      ...transitions
    }
  })
</script>

<style lang="scss" scoped>
.life-story {
  background-color: var(--color-light-grey);

  &__stories {
    @include blocks-y-spacing;

    background-color: var(--color-light-grey);
  }

  &__modal .loading-indicator {
    margin: rem(120px) auto 0;
  }

  &__new-story :deep(.cta) {
    background-color: var(--color-white);
  }

  &__new-story {
    @include blocks-padding-bottom;

    z-index: 4;

    @include mq(medium) {
      position: sticky;
      top: calc(var(--header-height) * 1.3);
    }

    >.l-wrapper {
      text-align: center;
    }
  }
}
</style>
